import {ISSUE_PERMITTED_ACTIONS} from '../constants/issue-types';
import {ATTACHMENT_PERMITTED_ACTIONS} from '../constants/attachments';
import lodashGet from 'lodash/get';
import {ISSUE_ATTRIBUTE_NAMES} from '../constants/issue-attribute-names';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';

export const canAddComment = ({permittedActions = []}) =>
  permittedActions.includes(ISSUE_PERMITTED_ACTIONS.ADD_COMMENT);

export const canAddAttachment = ({permittedActions = []}) =>
  permittedActions.includes(ISSUE_PERMITTED_ACTIONS.ADD_ATTACHMENT);

export const canDeleteAttachment = attachment => {
  const attachmentPermittedActions = attachment.permittedActions || [];
  return attachmentPermittedActions.includes(ATTACHMENT_PERMITTED_ACTIONS.REMOVE_ATTACHMENT);
};

export const issueLink = {
  _func: null,
  get func() {
    return this._func;
  },
  set func(f) {
    this._func = typeof f === 'function' ? f : null;
  }
};

export const hasPermittedAttribute = (issue, attribute) =>
  (issue[ISSUE_ATTRIBUTE_NAMES.PERMITTED_ATTRIBUTES] || []).includes(
    Array.isArray(attribute) ? attribute[0] : attribute.replace(/\..*$/, '')
  );

export const hasLinkedDocument = issue => issue?.linkedDocuments?.length > 0;

/**
 * This function should not be used to check document existence;
 * use `hasLinkedDocument` instead.
 */
export const getLinkedDocument = issue => (hasLinkedDocument(issue) ? issue.linkedDocuments[0] : {});

export const getUrn = issue => getLinkedDocument(issue).urn;

export const doesViewableHaveSheetInfo = viewable =>
  !!viewable &&
  (!(viewable.is3D === null && viewable.name === null && viewable.id === null) ||
    !(
      viewable.is3D === null &&
      viewable.name === null &&
      viewable.id === null &&
      viewable.guid === null &&
      viewable.viewableId === null
    ));

export const isSameReferredSheet = (currSheet, issueSheet) =>
  (currSheet.sheetGuid === issueSheet.guid && !!issueSheet.guid) ||
  (currSheet.viewableId === issueSheet.viewableId && !!issueSheet.viewableId) ||
  (currSheet.viewableId === issueSheet.guid && !!issueSheet.guid) || // viewableId stored as GUID
  currSheet.sheetName === issueSheet.name || // bad data check
  currSheet.sheetGuid === issueSheet.id;

export const isIssueInSheet = (issue, currentSheets = []) => {
  const issueSheet = lodashGet(getLinkedDocument(issue), 'details.viewable');
  return (
    currentSheets.length === 0 ||
    !doesViewableHaveSheetInfo(issueSheet) ||
    !!currentSheets.find(sheet => isSameReferredSheet(sheet, issueSheet))
  );
};

export const getNonEmpties = obj => pickBy(obj, value => !isEmpty(value) || isBoolean(value) || isNumber(value));

export const withoutIssueTypeId = obj => pickBy(obj, (_, key) => key !== ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID);

export const getRfiFormattedLinkedDocument = issue => {
  if (!hasLinkedDocument(issue)) {
    return null;
  }
  const {urn, details = {}} = getLinkedDocument(issue);
  return {
    urn,
    pushpinAttributes: {
      externalId: details.externalId,
      objectId: details.objectId,
      attributesVersion: details.viewerState?.attributesVersion,
      position: details.position
    }
  };
};

export const getRfiFormattedAttachmentsAttributes = attachments =>
  attachments?.map(({name, urn, urnType}) => ({
    name,
    urn,
    urnType
  }));

export const getRfiFormattedSheetWithViewableId = issue => {
  if (!hasLinkedDocument(issue)) {
    return null;
  }

  const {viewable} = getLinkedDocument(issue)?.details ?? {};
  if (!viewable) {
    return null;
  }
  return {
    is3D: viewable.is3D,
    sheetGuid: viewable.guid,
    sheetName: viewable.name,
    viewableId: viewable.viewableId
  };
};
